import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { catchError, EMPTY, finalize, first, map, shareReplay, startWith, tap } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { AppGlobalControlValues } from 'src/app/common/app.global.control.values';
import { promiseWithCatch } from 'src/app/common/app.helpers';
import { OperationInfoService } from 'src/app/common/long-operations/operation-info.service';
import ProjectLinks from 'src/app/common/project.links';
import { Session } from 'src/app/interfaces';
import { PaginationResolver } from 'src/app/interfaces/pagination.interface';
import { TableCellOnActionEvent } from 'src/app/interfaces/tables.interfaces';
import { ItemStatus } from 'src/app/interfaces/views.interface';
import { DialogActionTypes } from 'src/app/notifications/dialogs/dialogs.enum';
import { NotificationContextAttributesService } from 'src/app/notifications/notifications-context-attributes.service';
import {
  PaginatableListOfBasicUsers,
  PostBasic,
  Posts,
  Project,
  ProjectBasic,
  ProjectsBasic,
  UserBasic,
} from 'src/app/swagger-api';
import { CardType } from 'src/app/views/cards/cards.types';
import { PostsService } from '../../posts/posts.service';
import { initialPaginationState } from '../../shared/social-initiative/social-initiative.helpers';
import { ManageProjectService } from '../manage-project.service';

type ProjectPageTabs = 'project_info' | 'project_posts' | 'project_members';

@Component({
  selector: 'app-project-detail-info',
  templateUrl: './project-detail-info.component.html',
  styleUrls: ['./project-detail-info.component.scss'],
  providers: [PostsService],
})
export class ProjectDetailInfoComponent {
  public readonly CardType: typeof CardType = CardType;
  public readonly EXPIRED: ItemStatus = ItemStatus.EXPIRED;
  public readonly EMPTY: ItemStatus = ItemStatus.EMPTY;
  public readonly LINK_PROJECTS_LIST: string = ProjectLinks.LINK_PROJECTS;
  public readonly LINK_PROJECTS_MANAGE: string = ProjectLinks.LINK_PROJECTS_MANAGE;

  public posts: PostBasic[] = [];
  public members: UserBasic[] = [];
  public suggestedProjects: ProjectBasic[] = [];

  public pageSize = 10;
  public membersTotalCount = 0;
  public membersPageNumber = 1;

  public isSuggestedProjectsLoading = false;

  public resolver: PaginationResolver = {
    state: initialPaginationState(),
    onPageChanged: (page: number) => {
      this.posts = [];
      this.resolver.state.pagination.currentPage = page;
      this.postsService.fetchProjectPosts(this.id, this.resolver.state);
    },
  };

  private id: string;

  private tabTypes: { [key in ProjectPageTabs]: () => void } = {
    project_info: () => {
      //
    },
    project_posts: () => this.postsService.fetchProjectPosts(this.id, this.resolver.state),
    project_members: () => this.fetchProjectMembers(),
  };

  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private postsService = inject(PostsService);
  private sessionService = inject(SessionService);
  private operationInfoService = inject(OperationInfoService);
  private modalAttributesService = inject(NotificationContextAttributesService);
  private manageProjectService = inject(ManageProjectService, { skipSelf: true });

  public readonly content$ = this.manageProjectService.content$;

  public readonly counters$ = this.manageProjectService.projectTabsCountersChanges.pipe(
    startWith({ members_count: 0, post_cunt: 0 }),
  );

  public readonly membersTable$ = this.manageProjectService.membersTableDataChanges;

  public readonly isExpired$ = this.manageProjectService.isExpired$.pipe(shareReplay(1));
  public readonly isNavigable$ = this.sessionService.sessionChanges.pipe(
    map(({ token, adminSession }: Session) => token && !adminSession),
  );

  public readonly project$ = this.manageProjectService.entityChanged$.pipe(
    tap((project: Project) => {
      this.id = project.entity_id;

      this.getRelatedProjects();
      this.fetchProjectMembers();
      this.subscribeOnProjectPostsChanges();
      this.subscribeOnProjectMembersChanges();

      this.manageProjectService.fetchRequestProjectCounters(project.entity_id);
    }),
  );

  public onTabClick($event: NgbNavChangeEvent<ProjectPageTabs>): void {
    this.resetPaginationState();
    this.resolver.state = initialPaginationState();
    this.tabTypes[$event.nextId]();

    this.manageProjectService.fetchRequestProjectCounters(this.id);
  }

  public inviteToProject(): void {
    this.manageProjectService.navigateToInviteToEntitySearchPage(this.id);
  }

  public leaveProject(): void {
    this.operationInfoService.setupOperation(
      {
        spinner: 'button',
        button: { id: 'btn-project-leave' },
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
        successfulMessage: 'MANAGE_PROJECT_CONFIRM_LEAVE_HINT',
        confirmationDialog: {
          description: 'PROJECT_LEAVE_PROJECT_DESCRIPTION',
          title: 'PROJECT_LEAVE_PROJECT_TITLE',
          buttons: [
            this.modalAttributesService.createDialogButton('BTN_CLOSE_HINT'),
            this.modalAttributesService.createDialogButton(
              'PROJECT_LEAVE_PROJECT_BTN_HINT',
              'btn-primary',
              DialogActionTypes.CONFIRM_ACTION,
            ),
          ],
        },
      },
      () => {
        this.manageProjectService.leaveEntity(this.id, (this.membersPageNumber - 1) * this.pageSize, this.pageSize);
      },
    );
  }

  public onRemoveMember(event: TableCellOnActionEvent): void {
    this.operationInfoService.setupOperation(
      {
        spinner: 'button',
        button: { id: 'btn-join' },
        successfulMessage: 'MANAGE_PROJECT_CONFIRM_USER_REMOVE_HINT',
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
        confirmationDialog: {
          description: 'PROJECT_REMOVE_USER_DESCRIPTION',
          title: 'PROJECT_REMOVE_USER_TITLE',
          buttons: [
            this.modalAttributesService.createDialogButton('BTN_CLOSE_HINT'),
            this.modalAttributesService.createDialogButton(
              'BTN_REMOVE_HINT',
              'btn-primary',
              DialogActionTypes.CONFIRM_ACTION,
            ),
          ],
        },
      },
      () => {
        this.manageProjectService.removeMember(
          event.actorId,
          event.entityId,
          (this.membersPageNumber - 1) * this.pageSize,
          this.pageSize,
        );
      },
    );
  }

  public joinProject(): void {
    if (this.sessionService.isSessionActive()) {
      this.operationInfoService.setupOperation(
        {
          spinner: 'button',
          button: { id: 'btn-project-join' },
          successfulMessage: 'PROJECT_JOIN_REQUEST_DIALOG_DESCRIPTION',
          blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
        },
        () => {
          this.manageProjectService.joinEntity(this.id, (this.membersPageNumber - 1) * this.pageSize, this.pageSize);
        },
      );
    } else {
      this.redirectToLoginPage();
    }
  }

  public onMembersPageChanged(page: number): void {
    this.membersPageNumber = page;
    this.fetchProjectMembers();
  }

  public manageProjectLink(id: string): string[] {
    return [ProjectLinks.LINK_PROJECTS_MANAGE, id];
  }

  private redirectToLoginPage(): void {
    promiseWithCatch(
      this.router.navigate([ProjectLinks.LINK_LOGIN], {
        queryParams: { returnUrl: ProjectLinks.LINK_PROJECTS_VIEW + '/' + this.id },
      }),
    );
  }

  private resetPaginationState(): void {
    this.membersTotalCount = 0;
    this.membersPageNumber = 1;
    this.pageSize = 10;
  }

  private getRelatedProjects(): void {
    this.isSuggestedProjectsLoading = true;
    this.manageProjectService
      .getRelatedProjects(this.id, 10)
      .pipe(
        first(),
        takeUntilDestroyed(this.destroyRef),
        catchError(() => EMPTY),
        finalize(() => {
          this.isSuggestedProjectsLoading = false;
        }),
      )
      .subscribe((projects: ProjectsBasic): void => {
        this.suggestedProjects = projects.items;
      });
  }

  private fetchProjectMembers(): void {
    this.manageProjectService.fetchEntityMembers(this.id, (this.membersPageNumber - 1) * this.pageSize, this.pageSize);
  }

  private subscribeOnProjectPostsChanges(): void {
    this.postsService.postsChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((posts: Posts) => {
      this.posts = posts.items;
      this.resolver.state.pagination.totalItemsCount = posts.total_count;
    });
  }

  private subscribeOnProjectMembersChanges(): void {
    this.manageProjectService.membersChanged
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((paginatableUsers: PaginatableListOfBasicUsers) => {
        this.membersTotalCount = paginatableUsers.total_count;

        if (paginatableUsers.items.length === 0 && this.membersPageNumber > 1) {
          this.membersPageNumber -= 1;
          this.fetchProjectMembers();
        }
      });
  }
}
